import React, { forwardRef, useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import { generateBinQRUrl } from '../../config/qrConfig';
import './BinLabelPrint.css';

const BinLabelPrint = forwardRef(({ bins, labelType }, ref) => {
  if (!bins || bins.length === 0) return null;

  return (
    <div ref={ref} className={`bin-print-container ${labelType}-container`}>
      {bins.map((bin, idx) => (
        <LabelContent
          key={idx}
          bin={bin}
          labelType={labelType}
        />
      ))}
    </div>
  );
});

function LabelContent({ bin, labelType }) {
  const qrUrl = generateBinQRUrl(bin.bin_code);

  switch (labelType) {
    case 'floor':
      return <FloorLabelPrint bin={bin} qrUrl={qrUrl} />;
    case 'shelf':
      return <ShelfLabelPrint bin={bin} qrUrl={qrUrl} />;
    case 'pallet':
    default:
      return <PalletLabelPrint bin={bin} qrUrl={qrUrl} />;
  }
}

// Floor Label Print - 10x8 Landscape
function FloorLabelPrint({ bin, qrUrl }) {
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (barcodeRef.current && bin.bin_code) {
      try {
        JsBarcode(barcodeRef.current, bin.bin_code, {
          format: 'CODE128',
          width: 3,
          height: 80,
          displayValue: false,
          margin: 0
        });
      } catch (error) {
        console.error('Barcode generation failed:', error);
      }
    }
  }, [bin.bin_code]);

  return (
    <div className="bin-print-label floor-print">
      <div className="floor-print-content">
        <div className="floor-print-top-row">
          <div className="floor-print-left">
            <div className="floor-print-bin-code">{bin.bin_code}</div>
          </div>
          <div className="floor-print-right">
            <div className="floor-print-barcode">
              <svg ref={barcodeRef}></svg>
            </div>
            <div className="floor-print-qr">
              <QRCodeSVG value={qrUrl} size={250} level="M" includeMargin={false} />
            </div>
          </div>
        </div>
        {bin.set_sku && (
          <div className="floor-print-sku-row">
            <div className="floor-print-set-sku">{bin.set_sku}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// Shelf Label Print - 1x3
function ShelfLabelPrint({ bin, qrUrl }) {
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (barcodeRef.current && bin.bin_code) {
      try {
        JsBarcode(barcodeRef.current, bin.bin_code, {
          format: 'CODE128',
          width: 1.5,
          height: 40,
          displayValue: false,
          margin: 0
        });
      } catch (error) {
        console.error('Barcode generation failed:', error);
      }
    }
  }, [bin.bin_code]);

  return (
    <div className="bin-print-label shelf-print">
      <div className="shelf-print-content">
        <div className="shelf-print-qr">
          <QRCodeSVG value={qrUrl} size={55} level="M" includeMargin={false} />
        </div>
        <div className="shelf-print-barcode">
          <svg ref={barcodeRef}></svg>
        </div>
        <div className="shelf-print-text">
          <div className="shelf-print-bin-code">{bin.bin_code}</div>
        </div>
      </div>
    </div>
  );
}

// Pallet Label Print - 4x6
function PalletLabelPrint({ bin, qrUrl }) {
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (barcodeRef.current && bin.bin_code) {
      try {
        JsBarcode(barcodeRef.current, bin.bin_code, {
          format: 'CODE128',
          width: 2.5,
          height: 70,
          displayValue: false,
          margin: 0
        });
      } catch (error) {
        console.error('Barcode generation failed:', error);
      }
    }
  }, [bin.bin_code]);

  return (
    <div className="bin-print-label pallet-print">
      <div className="pallet-print-content">
        <div className="pallet-print-header">
          <div className="pallet-print-bin-code">{bin.bin_code}</div>
        </div>
        <div className="pallet-print-body">
          <div className="pallet-print-left">
            <div className="pallet-print-barcode">
              <svg ref={barcodeRef}></svg>
            </div>
          </div>
          <div className="pallet-print-right">
            <QRCodeSVG value={qrUrl} size={130} level="M" includeMargin={false} />
            <div className="pallet-print-qr-label">SCAN</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BinLabelPrint;
